import React, { FC } from "react";
import { Box, Link as MuiLink, Typography } from "@mui/material";
import OpenInNewIcon from "@mui/icons-material/OpenInNew";
import { Link } from "react-router-dom";
import {
  LiveBoardTrip,
  SILENT_MIN,
  fmtAgo,
  fmtLocal,
  fmtRel,
  hasBackhaul,
  isLate,
  isOverdue,
  isPotLate,
  isSilent,
  isStale,
  notArrived,
  tripHref,
  viaBackhaul,
} from "map/liveBoard";
import { BOARD_COLORS, colorFor } from "./palette";

/** The plain-language version of the leg, for people who read the list rather than the map. */
const baseStatus = (t: LiveBoardTrip): string => {
  switch (t.leg) {
    case "pending":
      return "Scheduled";
    case "to_pickup":
      return "Heading to pickup";
    case "return":
      return "Crew dropped · returning to hub";
    case "dropoff":
      return "Ended · awaiting close";
    default:
      return t.pickup_completed_utc ? "Crew aboard" : "At pickup · next: dropoff";
  }
};

const TripRow: FC<{
  trip: LiveBoardTrip;
  selected: boolean;
  hovered: boolean;
  hubFilter: Set<string> | null;
  onSelect: () => void;
  onHover: (on: boolean) => void;
}> = ({ trip: t, selected, hovered, hubFilter, onSelect, onHover }) => {
  const color = colorFor(t);
  const stale = isStale(t);
  const overdue = isOverdue(t);

  const status = overdue ? (
    <Box component="span" sx={{ color: BOARD_COLORS.late, fontWeight: 600 }}>
      Late · past pickup, not started
    </Box>
  ) : notArrived(t) ? (
    <>
      {baseStatus(t)} ·{" "}
      <Box component="span" sx={{ color: BOARD_COLORS.late, fontWeight: 600 }}>
        late · not arrived +{t.late_minutes}m
      </Box>
    </>
  ) : isLate(t) ? (
    <>
      {baseStatus(t)} ·{" "}
      <Box component="span" sx={{ color: BOARD_COLORS.late, fontWeight: 600 }}>
        late +{t.late_minutes}m
      </Box>
    </>
  ) : isPotLate(t) ? (
    <>
      {baseStatus(t)} ·{" "}
      <Box component="span" sx={{ color: BOARD_COLORS.warn, fontWeight: 600 }}>
        potential late
      </Box>
    </>
  ) : (
    baseStatus(t)
  );

  // A pickup time in the past only reads as a problem while the crew has not been collected.
  const pickupPassed = t.minutes_to_pickup < 0 && t.leg !== "to_dropoff" && t.leg !== "dropoff";

  return (
    <Box
      role="button"
      tabIndex={0}
      onClick={onSelect}
      onMouseEnter={() => onHover(true)}
      onMouseLeave={() => onHover(false)}
      onKeyDown={(e) => {
        if (e.key === "Enter" || e.key === " ") {
          e.preventDefault();
          onSelect();
        }
      }}
      sx={{
        display: "flex",
        gap: 0.75,
        px: 1,
        py: 0.75,
        borderBottom: 1,
        borderColor: "divider",
        borderLeft: `4px solid ${color}`,
        cursor: "pointer",
        bgcolor: selected ? "action.selected" : hovered ? "action.hover" : "transparent",
      }}
    >
      <Box sx={{ flex: 1, minWidth: 0, fontSize: 12 }}>
        <Box sx={{ display: "flex", gap: 0.75, alignItems: "baseline" }}>
          <Box component="span" sx={{ fontFamily: "monospace", fontWeight: 700 }}>
            #{t.tripID}
          </Box>
          <Box component="span" sx={{ color: "text.secondary" }}>
            {status}
          </Box>
        </Box>
        <Box sx={{ color: "text.primary" }}>
          {t.driver_name ? (
            <b>{t.driver_name}</b>
          ) : (
            <Box component="b" sx={{ color: BOARD_COLORS.warn }}>
              No driver
            </Box>
          )}
          {t.vehicle_unit ? (
            ` · unit ${t.vehicle_unit}`
          ) : (
            <>
              {" · "}
              <Box component="b" sx={{ color: BOARD_COLORS.warn }}>
                no vehicle
              </Box>
            </>
          )}
          {` · ${t.hub} · ${t.client}`}
          {t.trainNumber ? ` · ${t.trainNumber}` : ""}
        </Box>
        <Box sx={{ color: "text.secondary" }}>
          {t.pickup_name} → {t.dropoff_name}
        </Box>
        <Box sx={{ color: "text.secondary" }}>
          {t.last_event ? `${t.last_event} ${fmtAgo(t.minutes_since_last_event)}` : "no events yet"}
        </Box>
        {hasBackhaul(t) ? (
          <Box sx={{ color: BOARD_COLORS.backhaul }}>
            ↩ backhaul{viaBackhaul(t, hubFilter) ? " into this hub" : ""}: {t.backhaul_hubs}
          </Box>
        ) : null}
      </Box>
      <Box sx={{ textAlign: "right", fontSize: 11, whiteSpace: "nowrap" }}>
        <Box sx={{ fontWeight: 700 }}>
          {fmtLocal(t.pickup_local)} {t.trip_tz}
        </Box>
        <Box sx={{ color: pickupPassed ? (overdue ? BOARD_COLORS.late : BOARD_COLORS.warn) : "text.secondary" }}>
          {fmtRel(t.minutes_to_pickup)}
        </Box>
        {t.leg === "pending" ? null : (
          <>
            <Box sx={{ color: stale ? BOARD_COLORS.warn : "text.secondary" }}>
              GPS {t.vehicle_gps_age_min == null ? "none" : fmtAgo(t.vehicle_gps_age_min)}
            </Box>
            {isSilent(t) ? <Box sx={{ color: BOARD_COLORS.warn }}>silent {SILENT_MIN}m+</Box> : null}
          </>
        )}
        <MuiLink
          component={Link}
          to={tripHref(t)}
          target="_blank"
          rel="noopener"
          onClick={(e) => e.stopPropagation()}
          title={`Open trip ${t.tripID} in Flow`}
          sx={{ display: "inline-flex", alignItems: "center", gap: 0.25, fontSize: 11 }}
        >
          Flow <OpenInNewIcon sx={{ fontSize: 11 }} />
        </MuiLink>
      </Box>
    </Box>
  );
};

const TripList: FC<{
  trips: LiveBoardTrip[];
  selectedId: number | null;
  hoveredId: number | null;
  hubFilter: Set<string> | null;
  onSelect: (id: number) => void;
  onHover: (id: number | null) => void;
}> = ({ trips, selectedId, hoveredId, hubFilter, onSelect, onHover }) => {
  if (!trips.length) {
    return (
      <Typography sx={{ p: 2, fontSize: 12, color: "text.secondary" }}>No trips match these filters.</Typography>
    );
  }
  return (
    <Box>
      {trips.map((t) => (
        <TripRow
          key={t.tripID}
          trip={t}
          selected={selectedId === t.tripID}
          hovered={hoveredId === t.tripID}
          hubFilter={hubFilter}
          onSelect={() => onSelect(t.tripID)}
          onHover={(on) => onHover(on ? t.tripID : null)}
        />
      ))}
    </Box>
  );
};

export default TripList;
